import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError, formatDate } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { Complaint, IntegrityCase, IntegrityCaseStatus, IntegrityCategory, Page } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner, StatusBadge, Tabs } from '../../ui/components';

const ACCS_ROLES = ['ACCS_INVESTIGATOR'];
const CATEGORIES: IntegrityCategory[] = ['BRIBERY', 'ABUSE_OF_OFFICE', 'PROCUREMENT_FRAUD', 'ASSET_ILLICIT_ENRICHMENT', 'EMBEZZLEMENT', 'CONFLICT_OF_INTEREST', 'OTHER_CORRUPTION'];
const STATUSES: IntegrityCaseStatus[] = [
  'OPENED',
  'PRELIMINARY_REVIEW',
  'FULL_INVESTIGATION',
  'REFERRED_TO_PROSECUTION',
  'CLOSED_INSUFFICIENT_EVIDENCE',
  'CLOSED_UNSUBSTANTIATED',
  'CLOSED_ADMIN_ACTION',
  'CLOSED_REFERRED_OUT',
];

/**
 * GET /integrity/cases — "my caseload", need-to-know scoped to cases THIS
 * investigator is a team member of, not every case ACCS holds. Air-gapped by
 * design: see packages/modules/m40-ombudsman/src/service/integrityCaseAccess.ts.
 */
export function IntegrityCases() {
  const { persona } = useAuth();
  const isInvestigator = persona?.roles.includes('ACCS_INVESTIGATOR') ?? false;
  const navigate = useNavigate();
  const [tab, setTab] = useState<'caseload' | 'referrals'>('caseload');
  const [status, setStatus] = useState<IntegrityCaseStatus | ''>('');
  const [q, setQ] = useState('');
  const [showOpen, setShowOpen] = useState<Complaint | null | 'blank'>(null);

  const qp = new URLSearchParams({ limit: '50' });
  if (status) qp.set('status', status);
  if (q.trim()) qp.set('q', q.trim());
  const caseloadQ = useApiQuery<Page<IntegrityCase>>(isInvestigator ? `/api/v1/m40/integrity/cases?${qp.toString()}` : null);
  const referralsQ = useApiQuery<Page<Complaint>>(isInvestigator ? '/api/v1/m40/integrity/referrals?limit=50' : null);

  return (
    <div>
      <PageHeader
        title="Integrity Cases"
        subtitle="ACCS investigation workspace — air-gapped need-to-know: a case is only visible to its own team."
        actions={
          isInvestigator ? (
            <button className="btn-primary" onClick={() => setShowOpen('blank')}>
              + Open case
            </button>
          ) : undefined
        }
      />
      {!isInvestigator ? <RoleNotice roles={ACCS_ROLES} /> : null}

      {isInvestigator ? (
        <>
          <Tabs tabs={[{ key: 'caseload', label: 'My caseload' }, { key: 'referrals', label: 'ACCS referrals inbox' }]} value={tab} onChange={setTab} />

          {tab === 'caseload' ? (
            <div>
              <div className="mb-4 flex flex-wrap items-end gap-3">
                <Field label="Subject name / ref contains" className="min-w-[220px] flex-1">
                  <input className="field-input" value={q} onChange={(e) => setQ(e.target.value)} />
                </Field>
                <Field label="Status" className="!mb-0">
                  <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value as IntegrityCaseStatus | '')}>
                    <option value="">All</option>
                    {STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {s.replaceAll('_', ' ')}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
              {caseloadQ.error ? <ErrorNote message={caseloadQ.error} /> : null}
              <div className="card overflow-x-auto">
                {caseloadQ.loading ? (
                  <Spinner />
                ) : (caseloadQ.data?.items.length ?? 0) === 0 ? (
                  <EmptyState title="No cases on your team yet" hint="Open one below, convert a whistleblower report, or pull an ACCS referral." />
                ) : (
                  <table className="w-full">
                    <thead>
                      <tr>
                        <th className="table-head">Ref</th>
                        <th className="table-head">Subject</th>
                        <th className="table-head">Category</th>
                        <th className="table-head">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {caseloadQ.data!.items.map((c) => (
                        <tr key={c.id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/ombudsman/integrity-cases/${c.id}`)}>
                          <td className="table-cell font-mono text-xs">{c.refNo}</td>
                          <td className="table-cell font-medium">{c.subjectName}</td>
                          <td className="table-cell text-xs">{c.category.replaceAll('_', ' ')}</td>
                          <td className="table-cell">
                            <StatusBadge status={c.status} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          ) : (
            <div>
              {referralsQ.error ? <ErrorNote message={referralsQ.error} /> : null}
              <div className="card overflow-x-auto">
                {referralsQ.loading ? (
                  <Spinner />
                ) : (referralsQ.data?.items.length ?? 0) === 0 ? (
                  <EmptyState title="No unconsumed ACCS referrals" />
                ) : (
                  <table className="w-full">
                    <thead>
                      <tr>
                        <th className="table-head">Ref</th>
                        <th className="table-head">Subject MDA</th>
                        <th className="table-head">Category</th>
                        <th className="table-head">Filed</th>
                        <th className="table-head" />
                      </tr>
                    </thead>
                    <tbody>
                      {referralsQ.data!.items.map((c) => (
                        <tr key={c.id}>
                          <td className="table-cell font-mono text-xs">{c.refNo}</td>
                          <td className="table-cell font-medium">{c.subjectMda}</td>
                          <td className="table-cell text-xs">{c.category.replaceAll('_', ' ')}</td>
                          <td className="table-cell text-xs">{formatDate(c.createdAt)}</td>
                          <td className="table-cell text-right">
                            <button className="btn-primary !px-2.5 !py-1 text-xs" onClick={() => setShowOpen(c)}>
                              Open as case
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}
        </>
      ) : null}

      {showOpen ? (
        <OpenCaseModal
          fromComplaint={showOpen === 'blank' ? null : showOpen}
          onClose={() => setShowOpen(null)}
          onDone={(created) => {
            setShowOpen(null);
            navigate(`/ombudsman/integrity-cases/${created.id}`);
          }}
        />
      ) : null}
    </div>
  );
}

function OpenCaseModal({ fromComplaint, onClose, onDone }: { fromComplaint: Complaint | null; onClose: () => void; onDone: (c: IntegrityCase) => void }) {
  const [subjectName, setSubjectName] = useState('');
  const [subjectMda, setSubjectMda] = useState(fromComplaint?.subjectMda ?? '');
  const [category, setCategory] = useState<IntegrityCategory>('BRIBERY');
  const [allegationSummary, setAllegationSummary] = useState(fromComplaint?.narrative ?? '');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const created = await api.post<IntegrityCase>('/api/v1/m40/integrity/cases', {
        subjectName,
        subjectMda: subjectMda || undefined,
        category,
        allegationSummary,
        originComplaintId: fromComplaint?.id,
      });
      onDone(created);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to open case');
    } finally {
      setBusy(false);
    }
  };

  const valid = subjectName.trim().length >= 2 && allegationSummary.trim().length >= 10;

  return (
    <Modal title={fromComplaint ? `Open case from ${fromComplaint.refNo}` : 'Open a proactive integrity case'} onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <p className="text-xs text-slate-500">You become this case&rsquo;s LEAD investigator.</p>
        <Field label="Subject name (the alleged wrongdoer)">
          <input className="field-input" value={subjectName} onChange={(e) => setSubjectName(e.target.value)} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Subject MDA (optional)">
            <input className="field-input" value={subjectMda} onChange={(e) => setSubjectMda(e.target.value)} />
          </Field>
          <Field label="Category">
            <select className="field-input" value={category} onChange={(e) => setCategory(e.target.value as IntegrityCategory)}>
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c.replaceAll('_', ' ')}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <Field label="Allegation summary">
          <textarea className="field-input" rows={3} value={allegationSummary} onChange={(e) => setAllegationSummary(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || !valid}>
            {busy ? 'Opening…' : 'Open case'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
